import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Button } from "../ui/button";
import { PopoverHeader, PopoverTitle, PopoverDescription, PopoverSeparator } from "../ui/popover";
import { Field, FieldDescription } from "../ui/field";
import { LevelMeter } from "../ui/level-meter";
import { useRoomController } from "../../room/RoomContext";
import { isDevelopmentDeployment } from "../../lib/deployment";
import { diagnosticCheckValue, qualitySeverityLabelKey } from "../../room/connection-quality";
import type {
  ConnectionQualityDetails,
  DetailSection,
  DetailValues,
  QualityKind,
} from "../../room/connection-quality";

// eslint-disable-next-line react-refresh/only-export-components -- The shared accordion hook intentionally accompanies its view; edits may reload importers.
export function useQualitySections(reset = 0) {
  const [section, setSection] = useState("");
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the open accordion section when the popover content changes; a render-phase adjustment would change paint timing
    setSection("");
  }, [reset]);
  return { section, setSection };
}

function ConnectionDetailSection({
  id,
  value,
  title,
  detail,
}: {
  id: string;
  value: DetailSection;
  title: string;
  detail: DetailValues | undefined;
}) {
  const { t } = useTranslation();
  const entries = Object.entries(detail?.values ?? {});
  return (
    <AccordionItem value={value} className="px-4">
      <AccordionTrigger id={`${id}Toggle`}>{title}</AccordionTrigger>
      <AccordionContent>
        {entries.length ? (
          <dl className="grid grid-cols-2 gap-x-3 text-xs [&>dd]:border-b [&>dd]:py-2 [&>dd]:text-right [&>dd]:wrap-anywhere [&>dt]:border-b [&>dt]:py-2 [&>dt]:wrap-anywhere [&>dt]:text-muted-foreground">
            {entries.map(([label, value]) => (
              <Fragment key={label}>
                <dt>{label}</dt>
                <dd>{value ?? "—"}</dd>
              </Fragment>
            ))}
          </dl>
        ) : (
          <FieldDescription>{detail?.emptyMessage || t("common.unavailable")}</FieldDescription>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

export function ConnectionDetailsBody({
  kind,
  details,
  section,
  setSection,
}: {
  kind: QualityKind;
  details: ConnectionQualityDetails | null;
  section: string;
  setSection: (_section: string) => void;
}) {
  const { t } = useTranslation();
  const { downloadMediaDiagnostics } = useRoomController();
  const overview = details?.overview;
  const title = overview
    ? overview.titleKey
      ? t(overview.titleKey)
      : overview.title
    : details?.summary || t("connection.collectingMeasurements");
  const names: Record<DetailSection, string> = {
    connection: "connection.details",
    video: "connection.videoDetails",
    audio: "connection.audioDetails",
  };
  return (
    <div id={`${kind}ConnectionDetailsBody`} className="grid gap-3">
      <PopoverHeader>
        <PopoverTitle id={`${kind}QualityTitle`}>{t("focused.connectionQuality")}</PopoverTitle>
        <div id={`${kind}QualitySummary`}>
          <PopoverDescription id={`${kind}QualityDescription`}>{title}</PopoverDescription>
          {overview ? (
            <LevelMeter
              scale="quality"
              level={overview.level}
              className="mt-3"
              aria-label={t("focused.connectionQuality")}
              aria-valuemin={0}
              aria-valuemax={4}
              aria-valuetext={t(qualitySeverityLabelKey(overview.severity))}
            />
          ) : null}
        </div>
      </PopoverHeader>
      <div
        id={`${kind}QualityChecks`}
        hidden={!overview?.checks.length}
        className="grid grid-cols-3 gap-2"
      >
        {overview?.checks.map((check, index) => (
          <Field
            key={`${check.label}-${index}`}
            data-status={check.status}
            className="min-w-0 rounded-md border p-2"
          >
            <FieldDescription className="wrap-anywhere">
              {check.labelKey ? t(check.labelKey) : check.label}
            </FieldDescription>
            <span
              className={`mt-auto text-sm font-medium wrap-anywhere ${check.status === "good" ? "text-quality-good" : check.status === "warn" ? "text-quality-warning" : check.status === "bad" ? "text-quality-critical" : "text-popover-foreground"}`}
            >
              {diagnosticCheckValue(check.value, t)}
            </span>
          </Field>
        ))}
      </div>
      <PopoverSeparator />
      {/* The raw statistics are a development aid; production deployments hide them. */}
      {isDevelopmentDeployment(document) ? (
        <>
          {/* Bleeds through the popover padding so item dividers span its full width. */}
          <Accordion
            type="single"
            collapsible
            value={section}
            onValueChange={setSection}
            className="-mx-4"
          >
            {(["connection", "video", "audio"] as const).map((section) => (
              <ConnectionDetailSection
                key={section}
                id={`${kind}${section[0].toUpperCase()}${section.slice(1)}Details`}
                value={section}
                title={t(names[section])}
                detail={details?.[section]}
              />
            ))}
          </Accordion>
          <PopoverSeparator />
        </>
      ) : null}
      <Button
        id={`${kind}DiagnosticsDownload`}
        onClick={downloadMediaDiagnostics}
        className="w-full"
      >
        {t("connection.downloadDiagnostics")}
      </Button>
    </div>
  );
}
